import { useId, type ReactElement } from "react";
import { avatarSpec, type AvatarSpec, type MaskKind } from "../lib/avatar";
import { useTheme } from "./ThemeContext";

function Mask({ kind, fill, eye }: { kind: MaskKind; fill: string; eye: string }): ReactElement {
  const stroke = { stroke: "#000", strokeWidth: 2.5, strokeLinejoin: "round" as const };
  switch (kind) {
    case "torba": // paper bag with cut eye holes
      return (
        <g>
          <path d="M26 30 L31 24 L36 30 L42 23 L48 30 L54 23 L60 30 L66 24 L70 30 L72 82 L24 82 Z" fill={fill} {...stroke} />
          <path d="M34 48 l10 -2 l-2 8 l-8 1 Z M54 46 l10 2 l-2 8 l-9 -2 Z" fill={eye} />
          <path d="M40 68 h16" stroke="#000" strokeWidth={3} strokeDasharray="3 3" />
        </g>
      );
    case "kar-maskesi": // balaclava: rounded hood with an eye band
      return (
        <g>
          <path d="M22 84 C20 40 32 20 48 20 C64 20 76 40 74 84 Z" fill={fill} {...stroke} />
          <rect x="30" y="42" width="36" height="14" rx="7" fill={eye} {...stroke} />
          <path d="M28 66 h40 M28 72 h40" stroke="#000" strokeOpacity={0.35} strokeWidth={2} />
        </g>
      );
    case "domino": // carnival domino mask on a scrap of newsprint
      return (
        <g>
          <rect x="24" y="28" width="48" height="52" fill="#f4efe2" transform="rotate(-4 48 54)" {...stroke} />
          <path d="M18 50 C26 36 40 40 48 46 C56 40 70 36 78 50 C72 64 58 60 48 56 C38 60 24 64 18 50 Z" fill={fill} {...stroke} />
          <ellipse cx="36" cy="50" rx="6" ry="4" fill={eye} />
          <ellipse cx="60" cy="50" rx="6" ry="4" fill={eye} />
        </g>
      );
    case "gaz-maskesi": // gas mask: round lenses, filter can
      return (
        <g>
          <path d="M26 34 C30 22 66 22 70 34 L72 62 C66 76 30 76 24 62 Z" fill={fill} {...stroke} />
          <circle cx="37" cy="45" r="8" fill={eye} {...stroke} />
          <circle cx="59" cy="45" r="8" fill={eye} {...stroke} />
          <rect x="39" y="62" width="18" height="18" rx="4" fill="#000" />
          <path d="M42 66 h12 M42 71 h12 M42 76 h12" stroke={fill} strokeWidth={2} />
        </g>
      );
    case "ekran": // old TV for a head, static on the screen
      return (
        <g>
          <path d="M38 22 L46 30 M58 22 L50 30" stroke="#000" strokeWidth={3} />
          <rect x="22" y="30" width="52" height="44" rx="6" fill={fill} {...stroke} />
          <rect x="28" y="36" width="34" height="32" rx="4" fill={eye} {...stroke} />
          <path d="M30 44 h30 M30 52 h22 M30 60 h28" stroke="#000" strokeOpacity={0.4} strokeWidth={2} />
          <circle cx="68" cy="44" r="2.5" fill="#000" />
          <circle cx="68" cy="54" r="2.5" fill="#000" />
        </g>
      );
    case "vizor": // welding visor: one dark slot
      return (
        <g>
          <path d="M24 30 L72 30 L76 78 L20 78 Z" fill={fill} {...stroke} />
          <rect x="30" y="42" width="36" height="12" fill={eye} {...stroke} />
          <circle cx="28" cy="36" r="2" fill="#000" />
          <circle cx="68" cy="36" r="2" fill="#000" />
        </g>
      );
  }
}

function Sticker({ s }: { s: AvatarSpec["sticker"] }): ReactElement {
  const t = `translate(${s.x} ${s.y})`;
  if (s.kind === "yildiz") {
    return <path transform={t} d="M0 -9 L2.6 -2.8 L9 -2.8 L3.8 1.2 L5.6 8 L0 4 L-5.6 8 L-3.8 1.2 L-9 -2.8 L-2.6 -2.8 Z" fill={s.fill} stroke="#000" strokeWidth={1.5} />;
  }
  if (s.kind === "simsek") {
    return <path transform={t} d="M2 -10 L-6 2 L0 2 L-3 10 L6 -3 L0 -3 Z" fill={s.fill} stroke="#000" strokeWidth={1.5} />;
  }
  return <circle transform={t} r={6} fill={s.fill} stroke="#000" strokeWidth={1.5} />;
}

export interface AvatarProps {
  seed: string;
  codeName: string;
  size?: number;
  className?: string;
}

/** Collage-mask avatar (decorative; the code name next to it is the accessible label). */
export function Avatar({ seed, codeName, size = 64, className }: AvatarProps): ReactElement {
  const theme = useTheme();
  const spec = avatarSpec(seed, theme);
  const uid = useId().replace(/:/g, "");
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 96 96"
      role="img"
      aria-label={`${codeName} maskesi`}
      data-mask={spec.mask}
    >
      <defs>
        <clipPath id={`c${uid}`}>
          <circle cx="48" cy="48" r="44" />
        </clipPath>
        <pattern id={`h${uid}`} width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="3" cy="3" r="1.3" fill={spec.halftone} fillOpacity={0.55} />
        </pattern>
      </defs>
      <g clipPath={`url(#c${uid})`}>
        <rect width="96" height="96" fill={spec.background} />
        {spec.scraps.map((s, i) => (
          <polygon key={i} points={s.points} fill={s.fill} stroke="#000" strokeWidth={1} />
        ))}
        <rect width="96" height="96" fill={`url(#h${uid})`} />
        <g transform={`rotate(${spec.tilt} 48 52)`}>
          <Mask kind={spec.mask} fill={spec.maskFill} eye={spec.eyeFill} />
        </g>
        <rect x={spec.tape.x} y={spec.tape.y} width="30" height="9" fill="#e8ff2a" fillOpacity={0.6} transform={`rotate(${spec.tape.angle} ${spec.tape.x + 15} ${spec.tape.y + 4})`} />
      </g>
      <circle cx="48" cy="48" r="44" fill="none" stroke={spec.ring} strokeWidth={5} />
      <circle cx="48" cy="48" r="47" fill="none" stroke="#000" strokeWidth={2} />
      <Sticker s={spec.sticker} />
    </svg>
  );
}
